/**
 * Apartsales — Route `/$country/$city/$developer/$complex/unit/$unit`
 *
 * SEO-friendly URL для подробной страницы юнита (§2.4 ТЗ).
 * Пример: /russia/sochi/ug-development/morskoi-briz/unit/a-08-101
 *
 * Workflow:
 *   1. Парсим 5 slug-сегментов
 *   2. resolveSlugToUnitId() → находит UUID одним запросом
 *   3. fetchUnitCard(UUID) → получаем DTO
 *   4. unitCardToProperty() + UnitDetailPage
 *
 * 404: если slug-комбинация не найдена → друужелюбная страница 404.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { UnitDetailPage } from '~/components/UnitDetailPage';
import { fetchUnitCard } from '~/lib/unit-card-api';
import { unitCardToProperty } from '~/lib/unit-card-adapter';
import { resolveSlugToUnitId } from '~/lib/slug-routing';

export const Route = createFileRoute('/$country/$city/$developer/$complex/unit/$unit')({
  component: UnitBySlugScreen,
});

function UnitBySlugScreen() {
  const { country, city, developer, complex, unit } = Route.useParams();

  // Шаг 1: slug-путь → UUID
  const { data: unitId, isLoading: resolving, error: resolveError } = useQuery({
    queryKey: ['unit-slug-resolve', country, city, developer, complex, unit],
    queryFn: () => resolveSlugToUnitId({ city, developer, complex, unit }),
    staleTime: 5 * 60_000,
  });

  // Шаг 2: UUID → UnitCard DTO
  const { data, isLoading: loadingCard, error: cardError } = useQuery({
    queryKey: ['unit-card', unitId],
    queryFn: () => unitId ? fetchUnitCard(unitId) : Promise.reject(new Error('No unitId')),
    enabled: !!unitId,
    staleTime: 60_000,
  });

  const isLoading = resolving || (unitId && loadingCard);
  const error = resolveError || cardError;

  if (isLoading) {
    return (
      <div className="min-h-screen px-4 py-6">
        <div className="aspect-[4/5] w-full animate-pulse rounded-[20px] bg-bg-3" />
        <div className="mt-6 space-y-3">
          <div className="h-4 w-1/3 animate-pulse rounded-full bg-bg-3" />
          <div className="h-3 w-2/3 animate-pulse rounded-full bg-bg-3" />
          <div className="h-3 w-1/2 animate-pulse rounded-full bg-bg-3" />
        </div>
      </div>
    );
  }

  if (!unitId && !resolving) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <div className="text-[22px] font-bold text-label">Юнит не найден</div>
        <div className="mt-2 text-[14px] text-label-2 max-w-[280px]">
          Возможно, юнит снят с продажи или ссылка устарела.
        </div>
        <Link
          to="/"
          className="mt-6 rounded-full bg-brand-blue px-5 py-2.5 text-[14px] font-semibold text-white transition-transform active:scale-95"
        >
          На главную
        </Link>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <div className="text-[20px] font-bold text-label">Не удалось загрузить юнит</div>
        <div className="mt-2 text-[14px] text-label-2 max-w-[280px]">
          {error ? (error as Error).message : 'Попробуйте обновить страницу.'}
        </div>
        <Link
          to="/"
          className="mt-6 rounded-full bg-brand-blue px-5 py-2.5 text-[14px] font-semibold text-white transition-transform active:scale-95"
        >
          На главную
        </Link>
      </div>
    );
  }

  const property = unitCardToProperty(data);
  return <UnitDetailPage property={property} />;
}
